import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { extractNestedAnswers } from "@/encounters/components/PostpartumEncounter/utils/postpartum";
import {
  PostpartumEmotionalHealthQuestionsDisplayNames,
  PostpartumEmotionalHealthQuestionsLinkIds,
  PostpartumEncounterQuestionnaireURLs,
} from "@/encounters/constants/postpartum-encounter";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";

const EmotionalHealthDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          PostpartumEncounterQuestionnaireURLs.EmotionalHealth,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  // If no emotional health data has ever been recorded
  if (!currentVersion) {
    return (
      <Paper p="md" withBorder>
        <Stack mb="lg">
          <Title order={2}>Emotional Health</Title>
        </Stack>
        <TextWrapper py="lg" c="gray.6" ta="center">
          No emotional health data has been recorded for this encounter.
        </TextWrapper>
      </Paper>
    );
  }

  // If emotional health data exists but no previous version to compare against,
  // create an empty previous version to reuse the existing diff logic
  const effectivePreviousVersion =
    previousVersion || ({ item: [] } as unknown as QuestionnaireResponse);

  // Extract values for all emotional health fields using extractNestedAnswers
  const previousAnswers = extractNestedAnswers(effectivePreviousVersion);
  const currentAnswers = extractNestedAnswers(currentVersion);

  const previousMood =
    previousAnswers[PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthMood]?.join(
      ", ",
    ) || "";
  const currentMood =
    currentAnswers[PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthMood]?.join(
      ", ",
    ) || "";

  const previousTasks =
    previousAnswers[PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthTasks]?.join(
      ", ",
    ) || "";
  const currentTasks =
    currentAnswers[PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthTasks]?.join(
      ", ",
    ) || "";

  const previousBonding =
    previousAnswers[
      PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthBonding
    ]?.join(", ") || "";
  const currentBonding =
    currentAnswers[
      PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthBonding
    ]?.join(", ") || "";

  const previousSuicide =
    previousAnswers[
      PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthSuicide
    ]?.join(", ") || "";
  const currentSuicide =
    currentAnswers[
      PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthSuicide
    ]?.join(", ") || "";

  const previousHallucinations =
    previousAnswers[
      PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthHallucinations
    ]?.join(", ") || "";
  const currentHallucinations =
    currentAnswers[
      PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthHallucinations
    ]?.join(", ") || "";

  const previousNotes =
    previousAnswers[PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthNotes]?.join(
      ", ",
    ) || "";
  const currentNotes =
    currentAnswers[PostpartumEmotionalHealthQuestionsLinkIds.PostpartumEmotionalHealthNotes]?.join(
      ", ",
    ) || "";

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Emotional Health</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthMood}
                </TextWrapper>
                <WordsDiff showRemoved current={currentMood} previous={previousMood} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthTasks}
                </TextWrapper>
                <WordsDiff showRemoved current={currentTasks} previous={previousTasks} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthBonding}
                </TextWrapper>
                <WordsDiff showRemoved current={currentBonding} previous={previousBonding} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthSuicide}
                </TextWrapper>
                <WordsDiff showRemoved current={currentSuicide} previous={previousSuicide} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthHallucinations
                  }
                </TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentHallucinations}
                  previous={previousHallucinations}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthNotes}
                </TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthMood}
                </TextWrapper>
                <WordsDiff current={currentMood} previous={previousMood} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthTasks}
                </TextWrapper>
                <WordsDiff current={currentTasks} previous={previousTasks} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthBonding}
                </TextWrapper>
                <WordsDiff current={currentBonding} previous={previousBonding} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthSuicide}
                </TextWrapper>
                <WordsDiff current={currentSuicide} previous={previousSuicide} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {
                    PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthHallucinations
                  }
                </TextWrapper>
                <WordsDiff current={currentHallucinations} previous={previousHallucinations} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>
                  {PostpartumEmotionalHealthQuestionsDisplayNames.PostpartumEmotionalHealthNotes}
                </TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default EmotionalHealthDiff;
